/**
 * The activity indicator's own copy. It is kept beside the component rather than
 * in the application dictionary for two reasons: the surface is self-contained,
 * and it needs plain-language stage names instead of the pipeline's own labels.
 *
 * Nothing here names a queue, a job type, a worker or a run. The indicator is an
 * observation of the member's own data — "what is happening to my material right
 * now" — never a view of the machinery and never a control that starts work.
 * The wording also never promises a retry: work that could not be processed is
 * stated as exactly that, because the read surface cannot prove another attempt
 * is coming.
 */
export const activityMessages = {
  label: "Sistem etkinliği",
  /** The button's purpose, appended to the state so the name stays actionable. */
  detailsHint: "Ayrıntı için açın",
  trigger: {
    // No count and no area name: the rail has one short line, and "something
    // has stopped" is the whole headline. What stopped is in the panel.
    blocked: "Bazı işler durdu",
    unknown: "Etkinlik bilinmiyor",
    // Türkçede sayıdan sonra çoğul eki gelmez; tek şablon her sayıyı karşılar.
    working: (count: number) => `Çalışıyor · ${count} öğe`,
    idle: "Sürmekte olan iş yok",
    unfinished: (count: number) => `${count} öğe tamamlanmadı`,
  },
  detail: {
    unknown: "Ne kadarının işlendiği şu anda bilinmiyor.",
    working: (inProgress: number, waiting: number) =>
      `${inProgress} sürüyor, ${waiting} bekliyor.`,
    idle: "Şu anda bekleyen ya da süren iş yok.",
    // Deliberately terminal wording. These items stopped; the indicator cannot
    // prove they will be attempted again, so it does not say they will be.
    // Kalıcı ton korunur: "işlenemedi", "henüz işlenmedi" değil.
    unfinished: (count: number) => `${count} öğe işlenemedi.`,
    recent: (count: number) => `Son 15 dakikada ${count} öğe tamamlandı.`,
    lastProcessed: (when: string) => `En son ${when} işlendi.`,
    asOf: (time: string) => `${time} itibarıyla`,
    stageInProgress: (count: number) => `${count} sürüyor`,
    stageWaiting: (count: number) => `${count} bekliyor`,
    stageUnfinished: (count: number) => `${count} tamamlanmadı`,
  },
  /**
   * A stopped part of the pipeline. The shared reason is stated once, in the
   * heading, and each line below it says only what is specific to that area —
   * repeating "this is not your fault" on every line would be noise.
   *
   * The wording claims nothing it cannot support. It does not say the work will
   * resume, it does not say how much is affected, and it never names the lane,
   * the queue or the upstream service that refused us.
   */
  blocked: {
    heading: "Bazı işler durdu ve yeniden başlamadı.",
    /** Nothing the member can do — stated plainly rather than left implied. */
    notYourDoing: "Bunun sebebi sizin yaptığınız bir şey değil.",
    area: (area: string, since: string) => `${area} — ${since} durdu.`,
    areaUndated: (area: string) => `${area} — durdu.`,
    /**
     * The one blocker a member *can* clear. It describes the condition, not an
     * action to take here: reconnecting lives on the connection surface, and
     * this is an indicator, not a control.
     */
    renewal: (area: string) =>
      `${area} — hesap bağlantısının yenilenmesi bekleniyor.`,
  },
  /**
   * Plain-language names for what is happening to the member's material. Each
   * one covers several internal pipeline stages; the grouping lives in
   * `model.ts` so the copy never has to name an internal stage.
   */
  stage: {
    collect: "Yeni öğeler toplanıyor",
    read: "Metin ve ekler okunuyor",
    understand: "Olgular ve taahhütler bulunuyor",
    publish: "Sorabilecekleriniz güncelleniyor",
    other: "Diğer işlemler",
  },
} as const;

export type ActivityMessages = typeof activityMessages;
